import React from 'react';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';
import { Box, Flex, Text, SimpleGrid } from '@chakra-ui/react';

const BrandLogo = ({ src, alt, color, size = 24 }) => (
  <img src={src} alt={alt} width={size} height={size} style={{ display: 'block' }} />
);

const liveIntegrations = [
  {
    name: 'Jira Software', description: 'Keep epics, issues, subtasks, and mapped fields aligned.',
    logo: <BrandLogo src="https://cdn.simpleicons.org/jira/0052CC" alt="Jira" />
  },
  {
    name: 'ClickUp', description: 'Bidirectional sync across spaces, folders, lists, and task relationships.',
    logo: <BrandLogo src="https://cdn.simpleicons.org/clickup/7B68EE" alt="ClickUp" />
  },
];

const roadmapTools = [
  { name: 'Notion', logo: 'https://cdn.simpleicons.org/notion/808080' },
  { name: 'Trello', logo: 'https://cdn.simpleicons.org/trello/0079BF' },
  { name: 'Linear', logo: 'https://cdn.simpleicons.org/linear/5E6AD2' },
  { name: 'Slack', logo: 'https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/slack.svg' },
];

const Integrations = () => {
  return (
    <Box
      as="section"
      id="integrations"
      bg="landing.surface"
      py={{ base: 24, md: 32 }}
      fontFamily="body"
      position="relative"
      overflow="hidden"
    >
      {/* Background Decor */}
      <Box 
        position="absolute" 
        bottom="-10%" left="0" 
        w="100%" h="50%" 
        bg="linear-gradient(to top, rgba(34, 211, 238, 0.04) 0%, transparent 100%)" 
        pointerEvents="none" 
      />
      
      <Box maxW="1100px" mx="auto" px={{ base: 5, md: 8 }} position="relative" zIndex={1}>
        <Box textAlign="center" mb={20}>
          <Flex
            display="inline-flex"
            align="center"
            gap={1.5}
            px={3} py={1.5}
            borderRadius="full"
            bg="brand.subtle"
            borderWidth="1px"
            borderColor="landing.border"
            mb={4}
            mx="auto"
          >
            <Zap size={14} color="brand.primary" />
            <Text fontSize="xs" fontWeight="700" color="brand.primary" letterSpacing="0.05em">
              INTEGRATIONS
            </Text>
          </Flex>
          <Text
            as="h2"
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="900"
            color="text.main"
            letterSpacing="-0.03em"
            mb={4}
          >
            Available Now for <Box as="span" color="brand.primary">Jira + ClickUp</Box>
          </Text>
          <Text fontSize="lg" color="text.muted" maxW="560px" mx="auto" lineHeight="1.7" textAlign={{ base: 'left', md: 'center' }}>
            DatLynk is built as a broader sync platform. Jira and ClickUp are available now, with additional connectors planned on the same structure-aware foundation.
          </Text>
        </Box>

        {/* Live integrations — full featured cards */}
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={5} mb={10}>
          {liveIntegrations.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Box
                p={6}
                borderRadius="2xl"
                borderWidth="2px"
                borderColor="brand.primary"
                bg="landing.card"
                boxShadow="0 0 0 1px rgba(37,99,235,0.08), 0 8px 24px rgba(37,99,235,0.08)"
                h="full"
                transition="all 300ms ease"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(37,99,235,0.15)',
                }}
              >
                <Flex align="center" justify="space-between" mb={3}>
                  <Flex align="center" gap={3}>
                    <Flex
                      w="52px" h="52px"
                      borderRadius="xl"
                      bg="white"
                      align="center"
                      justify="center"
                      boxShadow="0 2px 8px rgba(0,0,0,0.06)"
                      border="1px solid"
                      borderColor="gray.100"
                    >
                      {item.logo}
                    </Flex>
                    <Box>
                      <Text fontSize="lg" fontWeight="800" color="text.main">{item.name}</Text>
                      <Text fontSize="xs" color="text.muted" mt={0.5}>{item.description}</Text>
                    </Box>
                  </Flex>
                  <Flex align="center" gap={1.5} px={2.5} py={1} bg="success.50" borderRadius="full" border="1px solid" borderColor="success.100" flexShrink={0}>
                    <Box w={1.5} h={1.5} borderRadius="full" bg="success.500"
                      style={{ boxShadow: '0 0 4px rgba(52,211,153,0.7)' }} />
                    <Text fontSize="9px" fontWeight="800" color="success.600" letterSpacing="0.06em">LIVE</Text>
                  </Flex>
                </Flex>
              </Box>
            </motion.div>
          ))}
        </SimpleGrid>

        {/* Roadmap — muted inline row */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <Box
            p={5}
            borderRadius="xl"
            bg="landing.bg"
            borderWidth="1px"
            borderColor="landing.border"
          >
            <Text fontSize="xs" fontWeight="800" color="text.muted" letterSpacing="0.06em" mb={4}>
              PLANNED CONNECTORS
            </Text>
            <Flex gap={{ base: 4, md: 8 }} wrap="wrap" align="center">
              {roadmapTools.map((tool, i) => (
                <Flex key={i} align="center" gap={2} opacity={0.6}>
                  <img src={tool.logo} alt={tool.name} width={18} height={18} style={{ display: 'block' }} />
                  <Text fontSize="sm" fontWeight="600" color="text.secondary">{tool.name}</Text>
                </Flex>
              ))}
              <Text fontSize="xs" fontWeight="600" color="text.muted" fontStyle="italic">
                More connectors will be added over time
              </Text>
            </Flex>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Integrations;
